/**
 * Tax Calculate API service (S1-P3 — versioned formula-engine config + stateless /run).
 *
 * Promise-based wrapper around the real backend endpoints under
 * `api/v1/TaxCalculate`. Mirrors the axios/base-URL/cookie-auth + ResponseModel
 * `unwrap`/`toError` pattern of `services/taxConfigApi.js` / `services/taxQuestionApi.js`.
 * All responses are wrapped in the standard ResponseModel `{ message, status, data }`;
 * helpers below unwrap `.data` and throw on a non-success `status`
 * (APIStatus.Successful === 0) so callers can `await` the inner payload directly
 * and `catch` a friendly Error.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs.
 *
 * @typedef {Object} TaxCalcVersionDto
 * @property {number} calcVersionID @property {number} taxConfigVersionID @property {string} versionCode
 * @property {string} description @property {boolean} isActive
 *
 * @typedef {Object} TaxRuleFormulaDto
 * @property {number} formulaID @property {string} name @property {string} outputVar
 * @property {string} expression @property {?string} expressionGraphJson
 * @property {number} evalOrder @property {?string} notes @property {boolean} isActive
 *
 * @typedef {Object} TaxBracketDto
 * @property {number} bracketID @property {number} lowerBound @property {?number} upperBound
 * @property {number} rate @property {number} sortOrder
 *
 * @typedef {Object} TaxCalcConstantDto
 * @property {number} constantID @property {string} constKey @property {number} constValue @property {?string} notes
 *
 * @typedef {Object} TaxCalcFullDto
 * @property {TaxCalcVersionDto} version @property {TaxRuleFormulaDto[]} formulas
 * @property {TaxBracketDto[]} brackets @property {TaxCalcConstantDto[]} constants
 *
 * @typedef {Object} TaxVariableDto
 * @property {string} name @property {string} source @property {string} description @property {string} dataType
 *
 * @typedef {Object} TaxVariableCatalogDto
 * @property {TaxVariableDto[]} personalInfo @property {TaxVariableDto[]} globals
 * @property {TaxVariableDto[]} [paths]  the tax form's NESTED paths — `name` carries the full path text
 *   (`INCOME_40_5.subItems[].items[].amount`). OPTIONAL: the frontend degrades to an empty
 *   "เส้นทางข้อมูล" category when the group is absent (see taxAnnotate.buildVariableCatalog).
 *
 * @typedef {Object} TaxCalculateResultDto
 * @property {number} calcVersionID @property {number} totalIncome @property {number} expenseDeduction
 * @property {Object[]} deductionDetails  always empty — no producer since formulas stopped binding to rules
 * @property {number} totalDeductions  0 + a warning unless the version authors a `total_deductions` formula
 * @property {number} totalAllowances
 * @property {number} netIncome @property {number} progressiveTax @property {number} amtTax @property {boolean} usedAmt
 * @property {number} taxBeforeCredit @property {number} withholdingTax @property {number} taxToPay @property {number} taxRefund
 * @property {?Object} variableTrace @property {string[]} warnings
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) {
      return body.data;
    }
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    err.data = body.data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function patch(url, payload) {
  try {
    return unwrap(await http.patch(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxCalculate';

export const taxCalculateApi = {
  // ── Calc Versions ────────────────────────────────────────────────────
  /**
   * List calc versions, optionally filtered by the bound config version.
   * @param {?number} configVersionId
   * @param {boolean} includeInactive
   * @returns {Promise<TaxCalcVersionDto[]>}
   */
  getVersions(configVersionId = null, includeInactive = true) {
    let url = `${ROOT}/versions?includeInactive=${includeInactive}`;
    if (configVersionId != null) url += `&configVersionId=${configVersionId}`;
    return get(url);
  },
  /** @returns {Promise<TaxCalcVersionDto>} */
  getVersion(id) {
    return get(`${ROOT}/versions/${id}`);
  },
  /** @returns {Promise<TaxCalcFullDto>} version + formulas + brackets + constants */
  getFull(id, includeInactive = true) {
    return get(`${ROOT}/versions/${id}/full?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxCalcVersionDto>} */
  createVersion(body) {
    return post(`${ROOT}/versions`, body);
  },
  /** @returns {Promise<TaxCalcVersionDto>} */
  updateVersion(id, body) {
    return put(`${ROOT}/versions/${id}`, body);
  },
  /** @returns {Promise<boolean>} (siblings auto-deactivated server-side) */
  setVersionActive(id, isActive) {
    return patch(`${ROOT}/versions/${id}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} (formulas/brackets/constants cascade-deleted server-side) */
  deleteVersion(id) {
    return del(`${ROOT}/versions/${id}`);
  },
  /**
   * Persist the whole-canvas Vue-Flow graph blob (opaque; server does not parse it).
   * `canvasGraphJson === null` clears the stored layout. Echoes the full TaxCalcVersionDto.
   * @param {number} versionId
   * @param {?string} canvasGraphJson serialized canvas graph (nodes+edges+positions)
   * @returns {Promise<TaxCalcVersionDto>}
   */
  saveCanvasGraph(versionId, canvasGraphJson) {
    return put(`${ROOT}/versions/${versionId}/graph`, { canvasGraphJson });
  },

  // ── Formulas ─────────────────────────────────────────────────────────
  /** @returns {Promise<TaxRuleFormulaDto[]>} */
  getFormulas(versionId, includeInactive = true) {
    return get(`${ROOT}/versions/${versionId}/formulas?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxRuleFormulaDto>} */
  getFormula(formulaId) {
    return get(`${ROOT}/formulas/${formulaId}`);
  },
  /** @returns {Promise<TaxRuleFormulaDto>} */
  createFormula(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/formulas`, body);
  },
  /** @returns {Promise<TaxRuleFormulaDto>} */
  updateFormula(formulaId, body) {
    return put(`${ROOT}/formulas/${formulaId}`, body);
  },
  /** @returns {Promise<boolean>} */
  setFormulaActive(formulaId, isActive) {
    return patch(`${ROOT}/formulas/${formulaId}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteFormula(formulaId) {
    return del(`${ROOT}/formulas/${formulaId}`);
  },
  /**
   * Replace the whole formula set of a version (editor "Save") — transactional.
   * @param {number} versionId
   * @param {Object[]} formulasArray array of CreateTaxRuleFormulaRequest-shaped objects
   * @returns {Promise<TaxRuleFormulaDto[]>}
   */
  bulkSaveFormulas(versionId, formulasArray) {
    return put(`${ROOT}/versions/${versionId}/formulas/bulk`, { formulas: formulasArray || [] });
  },

  // ── Brackets ─────────────────────────────────────────────────────────
  /** @returns {Promise<TaxBracketDto[]>} ordered by sortOrder */
  getBrackets(versionId) {
    return get(`${ROOT}/versions/${versionId}/brackets`);
  },
  /** @returns {Promise<TaxBracketDto>} */
  createBracket(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/brackets`, body);
  },
  /** @returns {Promise<TaxBracketDto>} */
  updateBracket(bracketId, body) {
    return put(`${ROOT}/brackets/${bracketId}`, body);
  },
  /** @returns {Promise<boolean>} */
  deleteBracket(bracketId) {
    return del(`${ROOT}/brackets/${bracketId}`);
  },
  /**
   * Replace the whole bracket set of a version — transactional.
   * @param {number} versionId
   * @param {Object[]} bracketsArray array of CreateTaxBracketRequest-shaped objects
   * @returns {Promise<TaxBracketDto[]>}
   */
  bulkSaveBrackets(versionId, bracketsArray) {
    return put(`${ROOT}/versions/${versionId}/brackets/bulk`, { brackets: bracketsArray || [] });
  },

  // ── Constants ────────────────────────────────────────────────────────
  /** @returns {Promise<TaxCalcConstantDto[]>} */
  getConstants(versionId) {
    return get(`${ROOT}/versions/${versionId}/constants`);
  },
  /** @returns {Promise<TaxCalcConstantDto>} */
  createConstant(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/constants`, body);
  },
  /** @returns {Promise<TaxCalcConstantDto>} */
  updateConstant(constantId, body) {
    return put(`${ROOT}/constants/${constantId}`, body);
  },
  /** @returns {Promise<boolean>} */
  deleteConstant(constantId) {
    return del(`${ROOT}/constants/${constantId}`);
  },
  /**
   * Replace the whole constant set of a version — transactional.
   * @param {number} versionId
   * @param {Object[]} constantsArray array of CreateTaxCalcConstantRequest-shaped objects
   * @returns {Promise<TaxCalcConstantDto[]>}
   */
  bulkSaveConstants(versionId, constantsArray) {
    return put(`${ROOT}/versions/${versionId}/constants/bulk`, { constants: constantsArray || [] });
  },

  // ── Variables + Engine ───────────────────────────────────────────────
  /** @returns {Promise<TaxVariableCatalogDto>} read-only variable catalog */
  getVariables() {
    return get(`${ROOT}/variables`);
  },
  /**
   * Stateless compute — never persists. Returns the deduction breakdown + totals.
   * @param {number} calcVersionId
   * @param {{ personalInfo: Object, inputs: Object[], formData?: Object }} payload
   * @param {boolean} debug when true requests `?debug=true` (echoes variableTrace)
   * @returns {Promise<TaxCalculateResultDto>}
   */
  run(calcVersionId, payload, debug = false) {
    const url = `${ROOT}/versions/${calcVersionId}/run${debug ? '?debug=true' : ''}`;
    return post(url, {
      personalInfo: (payload && payload.personalInfo) || {},
      inputs: (payload && payload.inputs) || [],
      formData: formDataOf(payload),
    });
  },
  /**
   * Evaluate ONE sub-formula in isolation ("call a point") — the target OutputVar's dependency
   * closure only. Returns { outputVar, value, contributing[], itemBreakdown[], warnings }.
   * @param {number} calcVersionId
   * @param {string} targetOutputVar the sub-formula's OutputVar (e.g. 'expense_40_5')
   * @param {{ personalInfo?: Object, inputs?: Object[], formData?: Object }} payload
   * @returns {Promise<Object>} TaxPreviewFormulaResultDto
   */
  previewFormula(calcVersionId, targetOutputVar, payload) {
    return post(`${ROOT}/versions/${calcVersionId}/formulas/preview`, {
      targetOutputVar: String(targetOutputVar || ''),
      personalInfo: (payload && payload.personalInfo) || {},
      inputs: (payload && payload.inputs) || [],
      formData: formDataOf(payload),
    });
  },
};

/**
 * The nested `formData` block, keyed by RULE CODE — the channel a formula PATH resolves against
 * (`INCOME_40_5.subItems[].items[].amount`). See TaxCalculateRunRequest.FormData for the shape.
 *
 * It is sent on EVERY call, and that is the whole point of this helper. `inputs` alone is the LEGACY
 * flat shape: the engine repairs it by PROJECTING one subItem per row so a path formula still
 * computes — and attaches a permanent "ส่งมาในรูปแบบเดิม … ตรวจสอบผลลัพธ์ก่อนใช้งาน" warning saying it
 * guessed at the caller's intent. On a healthy request that warning is noise, and it sits in the same
 * list as the fall-through warning that means a real branch matched nothing; a list where one entry is
 * always present is a list nobody reads.
 *
 * `{}` (never null/undefined) when the caller has no nested data: the field is bound to a
 * `Dictionary<string, object>` that defaults to empty, so an explicit empty object and an omitted key
 * mean the same thing to the server — but only one of them makes the payload say what it means.
 * @param {?Object} payload
 * @returns {Object}
 */
function formDataOf(payload) {
  const fd = payload && payload.formData;
  return fd && typeof fd === 'object' && !Array.isArray(fd) ? fd : {};
}

export default taxCalculateApi;
